import React, {useState, useRef, useEffect} from "react";
import "./css/EditContractNote.css";
import {useRefreshContext} from "../context/RefreshContext";
import {deleteContractNote, saveContractNote} from "@portfolio/utils/PFService"; // Import the context
import Charges from "../importcnote/Charges"; // Import the new CSS file
import {usePopupStack, useStackedPopup} from "../../contexts/PopupStackContext";

const EditContractNote = ({clientId, contractNoteDTO, onClose}) => {
    const {contractNote, transactions, cnotesCharge} = contractNoteDTO || {};
    const formRef = useRef(null);
    const {refreshPFdata} = useRefreshContext(); // 🔹 Subscribe to global refresh
    const [charges, setCharges] = useState([]);
    const { push, pop } = usePopupStack();

// Maintain stockTransactions in state so they can be modified
    const [stockTransactions, setStockTransactions] = useState(
        (transactions || []).map((txn, index) => ({
            ...txn,
            id: index,
            totalValue: txn.quantity * txn.weightedAvgPrice + txn.brokerage
        }))
    );

/*    useEffect(() => {
        const popupEntry = { onClose }; // 🔥 Use the prop directly
        push(popupEntry);
        return () => pop();
    }, []);*/

    useStackedPopup(true, () => {
        // When ESC is pressed while this popup is top-most:
        if (onClose) {
            onClose();
        }
    });

    const handleClose = () => {
        onClose();
    };

    // Function to handle changes in Quantity, Price, and Brokerage
    const handleInputChange = (txnId, field, value) => {
        setStockTransactions((prevTransactions) =>
            prevTransactions.map((txn) =>
                txn.id === txnId
                    ? {
                        ...txn,
                        [field]: value,
                        totalValue:
                            (field === "quantity" ? value : txn.quantity) *
                            (field === "weightedAvgPrice" ? value : txn.weightedAvgPrice) +
                            (field === "brokerage" ? value : txn.brokerage)
                    }
                    : txn
            )
        );
    };

    const handleRemoveRow = (id) => {
        setStockTransactions(prevTransactions => {
            const updatedTransactions = [...prevTransactions]; // Create a copy

            // Find the correct index using the unique id
            const indexToRemove = updatedTransactions.findIndex(txn => txn.id === id);

            // If found, remove it
            if (indexToRemove !== -1) {
                updatedTransactions.splice(indexToRemove, 1);
            }

            return updatedTransactions; // Return updated array
        });
    };

    const handleDelete = async () => {
        try {
            const result =await deleteContractNote(contractNoteDTO.contractNote.contractNoteNo); // Wait for save to complete
            if (result.success) {
                refreshPFdata(); // ✅ Now refreshes AFTER the save is successful
                onClose(); // Close the popup after successful Save
            }
        } catch (error) {
            console.error("Error deleting ContractNote:", error);
        }
    };

    // Validate Buy and Sell Transactions
    const isValidTransaction = (stockTransactions, type, formData) => {
        return stockTransactions.every((txn) => {
            const script = formData.get(`${type}-script-${txn.id}`)?.trim();
            const quantity = formData.get(`${type}-quantity-${txn.id}`);
            const price = formData.get(`${type}-price-${txn.id}`);
            const brokerage = formData.get(`${type}-brokerage-${txn.id}`);
            const total = formData.get(`${type}-total-${txn.id}`);

            console.log(`Validating ${type} - ${txn.id}:`, {script, quantity, price, brokerage, total});

            if (!script) {
                alert(`${type.toUpperCase()} Transaction: Script is required.`);
                return false;
            }
            if (!quantity || isNaN(quantity)) {
                alert(`${type.toUpperCase()} Transaction: Quantity must be a number.`);
                return false;
            }
            if (!price || isNaN(price)) {
                alert(`${type.toUpperCase()} Transaction: Purchase Price must be a number.`);
                return false;
            }
            if (!brokerage || isNaN(brokerage)) {
                alert(`${type.toUpperCase()} Transaction: Brokerage must be a number.`);
                return false;
            }
            if (!total || isNaN(total)) {
                alert(`${type.toUpperCase()} Transaction: Total Amount must be a number.`);
                return false;
            }

            return true;
        });
    };

    const handleSave = async () => {
        const formData = new FormData(formRef.current);

        stockTransactions.forEach((txn) => {
            /*console.log(`Checking: script-${txn.id} =>`, formData.get(`script-${txn.id}`));
            console.log(`Checking: quantity-${txn.id} =>`, formData.get(`quantity-${txn.id}`));*/
        });

        if (!isValidTransaction(stockTransactions.filter(txn => txn.buyQuantity > 0), "buy", formData) ||
            !isValidTransaction(stockTransactions.filter(txn => txn.sellQuantity > 0), "sell", formData)) {
            return;
        }

        const newContractNote = {
            contractNote: {
                contractNoteNo: formData.get("contractNoteNo")?.trim(),
                settlementNo: contractNote.settlementNo,
                settlementDate: contractNote.settlementDate,
                tradeDate: formData.get("date"),
                clientId: clientId,
                brokerName: formData.get("broker")?.trim(),
                cnoteType: "CNOTE",
            },
            cnotesCharge: {
                contractNoteNo: formData.get("contractNoteNo")?.trim(),
                securitiesTransactionTaxNetTotal: parseFloat(formData.get("stt")) || 0.0,
                stampDutyNetTotal: parseFloat(formData.get("stamp")) || 0.0,
                gstTotal: cnotesCharge.gstTotal,
                payInPayOutObligationNetTotal: parseFloat(formData.get("net-payinout-obli")) || 0.0,
                otherCharges: cnotesCharge.gstTotal,
                netAmountNetTotal: parseFloat(formData.get("netamount")) || 0.0,
            },
            transactions: stockTransactions.map((txn) => {
                const transactionType = formData.get(`transactionType-${txn.id}`);
                const quantity = parseInt(formData.get(`quantity-${txn.id}`)) || 0;
                const totalValue = parseFloat(formData.get(`total-${txn.id}`)) || 0.0;

                return {
                    type:"StockTransactionDTO",
                    contractNoteNo: formData.get("contractNoteNo")?.trim(),
                    settlementNo: contractNote.settlementNo,
                    isin: txn.isin,
                    clientId: clientId,
                    symbol: formData.get(`script-${txn.id}`),
                    transactionType: transactionType,
                    quantity: quantity,
                    weightedAvgPrice: parseFloat(formData.get(`price-${txn.id}`)) || 0.0,
                    brokerage: parseFloat(formData.get(`brokerage-${txn.id}`)) || 0.0,
                    totalValue: totalValue,
                    netQuantity: transactionType === "Sell" ? -quantity : quantity, // Negative for Sell, Positive for Buy
                    netObligation: transactionType === "Sell" ? totalValue : -totalValue, // Positive for Sell, Negative for Buy
                    tradeDate: formData.get("date")
                };
            }),
            cnoteType: "CNOTE"
        };

        console.log("Saving Contract Note:", newContractNote);

        const result = await saveContractNote(newContractNote, true); // Wait for save to complete
        if (result.success) {
            refreshPFdata(); // ✅ Now refreshes AFTER the save is successful
            onClose(); // Close the popup after successful Save
        }
    };

    return (
        <div className="contract-note-overlay">
            <div className="contract-note-popup" onClick={(e) => e.stopPropagation()}>
                <form ref={formRef}>
                    {/* Header Section */}
                    <div className="top-container-cnote">
                        <div className="header-row">
                            <h3>Contract Note</h3>
                            <h3>{clientId}</h3>
                        </div>
                        <div className="header-field-container">
                            <div className="header-row-second">
                                <div className="form-group half-width">
                                    <label className="general-label">Date:</label>
                                    <input type="text" name="date" value={contractNote?.tradeDate || "N/A"} readOnly/>
                                </div>
                                <div className="form-group full-width">
                                    <label className="general-label">Client ID:</label>
                                    <input type="text" value={clientId} readOnly/>
                                </div>
                            </div>

                            <div className="header-row-second">
                                <div className="form-group half-width">
                                    <label className="general-label">Broker:</label>
                                    <input type="text" name="broker" value={contractNote?.brokerName || "N/A"}
                                           readOnly/>
                                </div>
                                <div className="form-group full-width">
                                    <label className="general-label">Contract Note No:</label>
                                    <input type="text" name="contractNoteNo"
                                           value={contractNote?.contractNoteNo || "N/A"}
                                           readOnly/>
                                </div>
                            </div>
                        </div>

                        <div className="transaction-table-container">
                            <h5>Transactions</h5>
                            <div className="contract-table-container">
                                <table className="contract-table">
                                    <thead>
                                    <tr>
                                        <th>Script</th>
                                        <th>Type</th>
                                        <th>Quantity</th>
                                        <th>Pur. Price</th>
                                        <th>Brokerage</th>
                                        <th>Total Amount</th>
                                        <th></th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {stockTransactions.map((txn) => (
                                        <tr key={`${txn.id}`}>
                                            <td><input type="text" name={`script-${txn.id}`}
                                                       className="transaction-input"
                                                       value={txn.symbol || "N/A"}/></td>
                                            <td><input type="text" name={`transactionType-${txn.id}`}
                                                       className="transaction-input"
                                                       value={txn.transactionType || "N/A"}/></td>
                                            <td><input type="number" name={`quantity-${txn.id}`}
                                                       className="transaction-input" defaultValue={txn.quantity || 0}
                                                       onChange={(e) =>
                                                           handleInputChange(txn.id, "quantity", parseFloat(e.target.value) || 0)
                                                       }/>
                                            </td>
                                            <td><input type="number" name={`price-${txn.id}`}
                                                       className="transaction-input"
                                                       value={txn.weightedAvgPrice?.toFixed(2) || "0.00"}
                                                       onChange={(e) =>
                                                           handleInputChange(txn.id, "weightedAvgPrice", parseFloat(e.target.value) || 0)
                                                       }/></td>
                                            <td><input type="number" name={`brokerage-${txn.id}`}
                                                       className="transaction-input"
                                                       value={txn.brokerage?.toFixed(2) || "0.00"}
                                                       onChange={(e) =>
                                                           handleInputChange(txn.id, "brokerage", parseFloat(e.target.value) || 0)
                                                       }/></td>
                                            <td><input type="number" name={`total-${txn.id}`}
                                                       className="transaction-input"
                                                       value={txn.totalValue?.toFixed(2) || "0.00"}/></td>
                                            <td>
                                                <button type="button" className="cross-button"
                                                        onClick={() => handleRemoveRow(txn.id)}>❌
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>

                                </table>
                            </div>
                        </div>

                        {/* Charges Section */}
                        <div>
                            <div className="charges-section">
                                <Charges initialCharges={cnotesCharge} onChargesUpdate={setCharges}/>
                            </div>
                        </div>

                        {/* Footer Buttons */}
                        <div className="contract-buttons">
                            <button type="button" className="delete-button" onClick={handleDelete}>Delete CNote
                            </button>
                            <div className="right-buttons">
                                <button type="button" className="save-button" onClick={handleSave}>Save</button>
                                <button type="button" className="cancel-button" onClick={handleClose}>Cancel</button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditContractNote;
